import React, { useState, useEffect, useRef } from 'react';
import {
  User, Package, Heart, Star, Settings, Camera, Edit3,
  ChevronRight, LogOut, Crown, Shield, Bell, Lock,
  MapPin, Phone, Mail, Calendar, Check, X, Eye, EyeOff, Trash2
} from 'lucide-react';
import gsap from 'gsap';
import { useNavigate, useParams } from 'react-router-dom';
import { getWishlist, removeFromWishlist, deleteReview, getUserReviews, getMyOrders, cancelOrder, addReview, BASE_URL, ENDPOINTS, getImageUrl } from '../apiConfig';
import './UserDashboard.css';

const TABS = [
  { id: 'profile',  label: 'My Profile',  icon: User },
  { id: 'orders',   label: 'My Orders',   icon: Package },
  { id: 'wishlist', label: 'Wishlist',    icon: Heart },
  { id: 'reviews',  label: 'Reviews',     icon: Star },
  { id: 'settings', label: 'Settings',    icon: Settings },
];

/* ── Mock data ── */
const MOCK_ORDERS = [
  { id: '#ESC-0012', name: 'Premium Oud', date: '2 May 2026', status: 'Delivered', price: 'PKR 30,000', img: null },
  { id: '#ESC-0011', name: 'Signature Noir', date: '18 Apr 2026', status: 'Shipped', price: 'PKR 19,000', img: null },
  { id: '#ESC-0010', name: 'Legacy Gold', date: '5 Apr 2026', status: 'Processing', price: 'PKR 22,000', img: null },
  { id: '#ESC-0009', name: 'Dark Knight', date: '12 Mar 2026', status: 'Delivered', price: 'PKR 25,000', img: null },
];

const STATUS_COLORS = {
  Delivered:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.2)' },
  delivered:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.2)' },
  Shipped:    { bg: 'rgba(212, 175, 55,0.1)', color: '#d4af37', border: 'rgba(212, 175, 55,0.25)' },
  shipped:    { bg: 'rgba(212, 175, 55,0.1)', color: '#d4af37', border: 'rgba(212, 175, 55,0.25)' },
  Processing: { bg: 'rgba(99,102,241,0.1)', color: '#818cf8', border: 'rgba(99,102,241,0.2)' },
  processing: { bg: 'rgba(99,102,241,0.1)', color: '#818cf8', border: 'rgba(99,102,241,0.2)' },
  Pending:    { bg: 'rgba(245,158,11,0.1)',  color: '#f59e0b', border: 'rgba(245,158,11,0.2)' },
  pending:    { bg: 'rgba(245,158,11,0.1)',  color: '#f59e0b', border: 'rgba(245,158,11,0.2)' },
  Cancelled:  { bg: 'rgba(239,68,68,0.1)',  color: '#f87171', border: 'rgba(239,68,68,0.2)' },
  cancelled:  { bg: 'rgba(239,68,68,0.1)',  color: '#f87171', border: 'rgba(239,68,68,0.2)' },
};

const MOCK_REVIEWS = [
  { _id: 'rev-1', product: 'Premium Oud', rating: 5, date: '3 May 2026', text: 'Absolutely divine. Long-lasting and rich with incredible sillage.' },
  { _id: 'rev-2', product: 'Signature Noir', rating: 4, date: '20 Apr 2026', text: 'Sophisticated and deep. Perfect for evenings out.' },
];

const UserDashboard = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [activeTab, setActiveTab] = useState(tab || 'profile');
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dob: '',
    address: ''
  });
  const [wishlistItems, setWishlistItems] = useState([]);
  const [userOrders, setUserOrders] = useState([]);
  const [userReviews, setUserReviews] = useState(MOCK_REVIEWS);
  const [editMode, setEditMode] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [notifs, setNotifs] = useState({ orders: true, promotions: true, wishlist: false });
  const [isSaving, setIsSaving] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '' });
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState(false);
  
  // Review Delete Modal states
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [showReviewDeleteSuccess, setShowReviewDeleteSuccess] = useState(false);
  const [isDeletingReview, setIsDeletingReview] = useState(false);

  // Order Cancel Modal states
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [showOrderCancelSuccess, setShowOrderCancelSuccess] = useState(false);
  const [isCancellingOrder, setIsCancellingOrder] = useState(false);

  // Order Delivered Popup & Star Review states
  const [deliveredOrderForPopup, setDeliveredOrderForPopup] = useState(null);
  const [showDeliveredIntroPopup, setShowDeliveredIntroPopup] = useState(false);
  const [showProductReviewModal, setShowProductReviewModal] = useState(false);
  const [showReviewSuccessPopup, setShowReviewSuccessPopup] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [activeReviewProductId, setActiveReviewProductId] = useState(null);
  const [submittingReviewForId, setSubmittingReviewForId] = useState(null);
  const [reviewRatings, setReviewRatings] = useState({}); // { [productId]: rating }
  const [reviewComments, setReviewComments] = useState({}); // { [productId]: comment }
  const [reviewSuccessForProducts, setReviewSuccessForProducts] = useState({}); // { [productId]: true }
  // eslint-disable-next-line no-unused-vars
  const [editingReviewProductId, setEditingReviewProductId] = useState({}); // { [productId]: true }

  const [allProducts, setAllProducts] = useState([]);

  // Utility to dynamically fetch high-res premium product images by ID, Name, or fallbacks
  const getProductImage = (productIdOrObj, name = '') => {
    if (!productIdOrObj && !name) return null;
    
    if (typeof productIdOrObj === 'string' && (productIdOrObj.startsWith('http') || productIdOrObj.startsWith('data:image'))) {
      return productIdOrObj;
    }

    if (typeof productIdOrObj === 'object' && productIdOrObj) {
      const img = productIdOrObj.images?.[0] || productIdOrObj.img || productIdOrObj.image;
      if (img) return getImageUrl(img);
    }
    
    const idStr = typeof productIdOrObj === 'object' && productIdOrObj 
      ? (productIdOrObj._id || productIdOrObj.id) 
      : String(productIdOrObj || '');

    // 1. Try matching by ID in product catalog
    if (idStr && idStr !== 'undefined') {
      const found = allProducts.find(p => p._id === idStr || p.id === idStr);
      if (found) {
        const img = found.images?.[0] || found.img || found.image;
        if (img) return getImageUrl(img);
      }
    }
    
    // 2. Try matching by Name in product catalog (essential for mock reviews)
    const nameToSearch = name || (typeof productIdOrObj === 'string' ? productIdOrObj : '');
    if (nameToSearch && nameToSearch.length > 2) {
      const foundByName = allProducts.find(p => p.name && (p.name.toLowerCase().includes(nameToSearch.toLowerCase()) || nameToSearch.toLowerCase().includes(p.name.toLowerCase())));
      if (foundByName) {
        const img = foundByName.images?.[0] || foundByName.img || foundByName.image;
        if (img) return getImageUrl(img);
      }
    }
    
    // 3. Fallback: Search in localStorage purchasedItems
    try {
      const purchased = JSON.parse(localStorage.getItem('purchasedItems') || '[]');
      const foundP = purchased.find(item => {
        const pId = item?.product?._id || item?.product?.id || item?.productId || item?._id || item?.id;
        const pName = item?.name || item?.product?.name || '';
        return (idStr && pId === idStr) || (nameToSearch && pName.toLowerCase().includes(nameToSearch.toLowerCase()));
      });
      if (foundP) {
        const img = foundP.product?.images?.[0] || foundP.product?.img || foundP.img || foundP.image;
        if (img) return getImageUrl(img);
      }
    } catch {}

    // 4. Fallback: Search in localStorage cartItems
    try {
      const cart = JSON.parse(localStorage.getItem('cartItems') || '[]');
      const foundC = cart.find(item => {
        const pId = item?.product?._id || item?.product?.id || item?.productId || item?._id || item?.id;
        const pName = item?.name || item?.product?.name || '';
        return (idStr && pId === idStr) || (nameToSearch && pName.toLowerCase().includes(nameToSearch.toLowerCase()));
      });
      if (foundC) {
        const img = foundC.product?.images?.[0] || foundC.product?.img || foundC.img || foundC.image;
        if (img) return getImageUrl(img);
      }
    } catch {}

    return null;
  };

  // Utility to dynamically translate product IDs to catalog names with proper fallbacks
  const getProductName = (productIdOrObj) => {
    if (!productIdOrObj) return 'Premium Fragrance';
    if (typeof productIdOrObj === 'object' && productIdOrObj) {
      if (productIdOrObj.name) return productIdOrObj.name;
      const innerId = productIdOrObj._id || productIdOrObj.id;
      if (innerId) productIdOrObj = innerId;
    }
    
    const idStr = String(productIdOrObj || '');
    if (!idStr || idStr === 'undefined') return 'Premium Fragrance';

    // 1. Try matching by ID in product catalog
    const found = allProducts.find(p => p._id === idStr || p.id === idStr);
    if (found && found.name) return found.name;

    // 2. Try matching by Name in product catalog (essential for mock reviews)
    const foundByName = allProducts.find(p => p.name && (p.name.toLowerCase() === idStr.toLowerCase() || p.name.toLowerCase().includes(idStr.toLowerCase())));
    if (foundByName && foundByName.name) return foundByName.name;

    // 3. Try matching in localStorage purchasedItems
    try {
      const purchased = JSON.parse(localStorage.getItem('purchasedItems') || '[]');
      const foundP = purchased.find(item => {
        const pId = item?.product?._id || item?.product?.id || item?.productId || item?._id || item?.id;
        const pName = item?.name || item?.product?.name || '';
        return pId === idStr || pName.toLowerCase() === idStr.toLowerCase();
      });
      if (foundP) {
        if (foundP.name) return foundP.name;
        if (foundP.product?.name) return foundP.product.name;
      }
    } catch {}

    // 4. Try matching in localStorage cartItems
    try {
      const cart = JSON.parse(localStorage.getItem('cartItems') || '[]');
      const foundC = cart.find(item => {
        const pId = item?.product?._id || item?.product?.id || item?.productId || item?._id || item?.id;
        const pName = item?.name || item?.product?.name || '';
        return pId === idStr || pName.toLowerCase() === idStr.toLowerCase();
      });
      if (foundC) {
        if (foundC.name) return foundC.name;
        if (foundC.product?.name) return foundC.product.name;
      }
    } catch {}

    // 5. If it is already a product name string (meaning it doesn't match a 24-character hex ID pattern)
    const isObjectId = /^[0-9a-fA-F]{24}$/.test(idStr);
    if (!isObjectId && idStr.length > 3 && !idStr.startsWith('local-')) {
      return idStr;
    }

    return 'Premium Fragrance';
  };

  // Utility to dynamically resolve item price with exhaustive fallbacks
  const getItemPrice = (item) => {
    if (!item) return 0;
    
    // 1. Check if price directly exists and is non-zero
    if (item.price !== undefined && item.price !== null && item.price !== 0 && item.price !== '0') {
      return parseInt(String(item.price).replace(/[^0-9]/g, '')) || 0;
    }
    
    // 2. Check in product object if populated
    const p = item.product || item.productId;
    if (p && typeof p === 'object') {
      const rawPrice = p.variants?.[0]?.finalPrice || p.variants?.[0]?.price || p.price;
      if (rawPrice) return parseInt(String(rawPrice).replace(/[^0-9]/g, '')) || 0;
    }
    
    const idStr = p && typeof p === 'object' ? (p._id || p.id) : String(p || '');
    
    if (idStr && idStr !== 'undefined') {
      // 3. Look up in state allProducts
      const found = allProducts.find(prod => prod._id === idStr || prod.id === idStr);
      if (found) {
        const rawPrice = found.variants?.[0]?.finalPrice || found.variants?.[0]?.price || found.price;
        if (rawPrice) return parseInt(String(rawPrice).replace(/[^0-9]/g, '')) || 0;
      }
      
      // 4. Look up in localStorage purchasedItems
      try {
        const purchased = JSON.parse(localStorage.getItem('purchasedItems') || '[]');
        const foundP = purchased.find(pItem => {
          const pId = pItem?.product?._id || pItem?.product?.id || pItem?.productId || pItem?._id || pItem?.id;
          return pId === idStr;
        });
        if (foundP) {
          const prodObj = foundP.product || foundP;
          const rawPrice = prodObj.variants?.[0]?.finalPrice || prodObj.variants?.[0]?.price || prodObj.price || foundP.price;
          if (rawPrice) return parseInt(String(rawPrice).replace(/[^0-9]/g, '')) || 0;
        }
      } catch {}

      // 5. Look up in localStorage cartItems
      try {
        const cart = JSON.parse(localStorage.getItem('cartItems') || '[]');
        const foundC = cart.find(pItem => {
          const pId = pItem?.product?._id || pItem?.product?.id || pItem?.productId || pItem?._id || pItem?.id;
          return pId === idStr;
        });
        if (foundC) {
          const prodObj = foundC.product || foundC;
          const rawPrice = prodObj.variants?.[0]?.finalPrice || prodObj.variants?.[0]?.price || prodObj.price || foundC.price;
          if (rawPrice) return parseInt(String(rawPrice).replace(/[^0-9]/g, '')) || 0;
        }
      } catch {}
    }
    
    return 0;
  };

  // Utility to dynamically resolve order total price with summing fallback
  const getOrderTotalPrice = (order) => {
    if (!order) return 0;
    
    // 1. Check direct properties
    const val = order.totalPrice || order.totalAmount || order.total || order.grandTotal || order.price;
    if (val !== undefined && val !== null && val !== 0 && val !== '0') {
      const parsed = parseInt(String(val).replace(/[^0-9]/g, '')) || 0;
      if (parsed > 0) return parsed;
    }
    
    // 2. Summing fallback
    if (order.items && order.items.length > 0) {
      return order.items.reduce((sum, item) => {
        const qty = item.qty || item.quantity || 1;
        const price = getItemPrice(item);
        return sum + (price * qty);
      }, 0);
    }
    
    return 0;
  };

  const contentRef = useRef(null);
  const heroRef = useRef(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (tab && TABS.find(t => t.id === tab)) {
      setActiveTab(tab);
    }
    
    const refreshData = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      // Fetch all products first to map images/names accurately
      fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`)
        .then(res => res.json())
        .then(prodData => {
          const parsed = Array.isArray(prodData.data) ? prodData.data : (Array.isArray(prodData) ? prodData : []);
          setAllProducts(parsed);
        })
        .catch(err => console.error("Error fetching products in UserDashboard:", err));

      const stored = localStorage.getItem('user');
      if (stored) {
        try { 
          const parsedUser = JSON.parse(stored);
          setUser(parsedUser);
          const name = parsedUser.fullName || parsedUser.name || parsedUser.username || '';
          const email = parsedUser.email || '';
          setFormData(prev => ({
            ...prev,
            fullName: name,
            email: email,
            phone: parsedUser.phone || prev.phone || '+92 300 0000000',
            dob: parsedUser.dob || prev.dob || '12/05/1998',
            address: parsedUser.address || prev.address || 'Phase 6, DHA, Karachi, Pakistan'
          }));
        } catch {}
      }
      try {
        setWishlistItems(JSON.parse(localStorage.getItem('wishlistItems') || '[]'));
      } catch { setWishlistItems([]); }

      if (token) {
        getWishlist().then(apiWishlist => {
          setWishlistItems(apiWishlist);
          localStorage.setItem('wishlistItems', JSON.stringify(apiWishlist));
        }).catch(err => {
          console.error("UserDashboard wishlist API error (using localStorage fallback):", err);
        });

        getUserReviews().then(apiReviews => {
          if (apiReviews && apiReviews.length > 0) {
            // Helper to find product name from ID or Object
            const getProdName = (prodVal) => {
              const resName = getProductName(prodVal);
              if (resName && resName !== 'Premium Fragrance') return resName;

              if (!prodVal) return 'Luxury Masterpiece Fragrance';
              if (typeof prodVal === 'object' && prodVal.name) return prodVal.name;
              const idStr = String(prodVal);
              try {
                const purchased = JSON.parse(localStorage.getItem('purchasedItems') || '[]');
                const foundP = purchased.find(item => item?.product?._id === idStr || item?.product?.id === idStr || item?.productId === idStr || item?._id === idStr);
                if (foundP && foundP.name) return foundP.name;
                if (foundP && foundP.product && typeof foundP.product === 'object' && foundP.product.name) return foundP.product.name;
                
                const cart = JSON.parse(localStorage.getItem('cartItems') || '[]');
                const foundC = cart.find(item => item?.product?._id === idStr || item?.product?.id === idStr || item?.productId === idStr || item?._id === idStr);
                if (foundC && foundC.name) return foundC.name;
              } catch {}
              return 'Luxury Masterpiece Fragrance';
            };

            const formattedReviews = apiReviews.map(r => {
              const rawP = r.productId || r.product;
              let pIdStr = '';
              if (rawP) {
                if (typeof rawP === 'object') {
                  pIdStr = String(rawP._id || rawP.id || '');
                } else {
                  pIdStr = String(rawP);
                }
              }
              return {
                _id: r._id || r.id,
                productId: pIdStr,
                product: getProdName(rawP),
                rating: r.rating || 5,
                date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Just now',
                text: r.comment || r.review || r.text || ''
              };
            });
            setUserReviews(formattedReviews);
          }
        }).catch(err => {
          console.error("UserDashboard reviews API error:", err);
        });

        getMyOrders().then(apiOrders => {
          let localOrders = [];
          try {
            localOrders = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
          } catch {}
          const merged = [...(apiOrders || [])];
          localOrders.forEach(lo => {
            const exists = merged.some(ao => ao._id === lo._id || ao.id === lo.id);
            if (!exists) merged.unshift(lo);
          });
          setUserOrders(merged);
          checkDeliveredOrders(merged);
        }).catch(err => {
          console.error("UserDashboard orders API error:", err);
          let localOrders = [];
          try {
            localOrders = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
          } catch {}
          if (localOrders.length > 0) {
            setUserOrders(localOrders);
            checkDeliveredOrders(localOrders);
          } else {
            setUserOrders(MOCK_ORDERS); // Fallback if API fails and no local cache
            checkDeliveredOrders(MOCK_ORDERS);
          }
        });
      }
    };

    refreshData();
    window.addEventListener('userUpdated', refreshData);
    window.addEventListener('storage', refreshData);
    window.addEventListener('wishlistUpdated', refreshData);

    const tl = gsap.timeline();
    tl.fromTo(heroRef.current,   { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' })
      .fromTo(sidebarRef.current, { opacity: 0, x: -40 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' }, '-=0.4')
      .fromTo(contentRef.current, { opacity: 0, x: 40 },  { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' }, '-=0.5');

    return () => {
      window.removeEventListener('userUpdated', refreshData);
      window.removeEventListener('storage', refreshData);
      window.removeEventListener('wishlistUpdated', refreshData);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Hook to freeze scroll locking when modal overlays are active
  useEffect(() => {
    const isModalOpen = orderToCancel || reviewToDelete || showOrderCancelSuccess || showReviewDeleteSuccess || deliveredOrderForPopup || showDeliveredIntroPopup || showProductReviewModal || showReviewSuccessPopup;
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [orderToCancel, reviewToDelete, showOrderCancelSuccess, showReviewDeleteSuccess, deliveredOrderForPopup, showDeliveredIntroPopup, showProductReviewModal, showReviewSuccessPopup]);

  // Set up auto-polling for fresh orders to detect delivered status changes in real-time
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) return;

    const pollOrders = async () => {
      try {
        const apiOrders = await getMyOrders();
        let localOrders = [];
        try {
          localOrders = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
        } catch {}
        
        // Merge API orders and local orders
        const merged = [...(apiOrders || [])];
        localOrders.forEach(lo => {
          const exists = merged.some(ao => ao._id === lo._id || ao.id === lo.id);
          if (!exists) merged.unshift(lo);
        });
        
        // Check if orders have changes
        setUserOrders(prev => {
          const hasChanges = prev.length !== merged.length || prev.some((o, i) => {
            const m = merged.find(x => x._id === o._id || x.id === o.id);
            if (!m) return true;
            const prevStatus = (o.status || o.orderStatus || '').toLowerCase();
            const newStatus = (m.status || m.orderStatus || '').toLowerCase();
            return prevStatus !== newStatus;
          });
          
          if (hasChanges) {
            checkDeliveredOrders(merged);
            return merged;
          }
          return prev;
        });
      } catch (err) {
        console.error("Auto-polling orders failed:", err);
      }
    };

    // Poll every 8 seconds
    const intervalId = setInterval(pollOrders, 8000);
    return () => clearInterval(intervalId);
  }, []);

  const removeItem = async (id) => {
    const updated = wishlistItems.filter(item => item.id !== id && item._id !== id);
    setWishlistItems(updated);
    localStorage.setItem('wishlistItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('wishlistUpdated'));

    const token = localStorage.getItem('token');
    if (token) {
      try {
        await removeFromWishlist(id);
      } catch (err) {
        console.error("Failed to remove from wishlist API:", err);
      }
    }
  };

  const handleDeleteUserReview = (reviewId) => {
    setReviewToDelete(reviewId);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete) return;
    setIsDeletingReview(true);
    try {
      if (reviewToDelete) {
        await deleteReview(reviewToDelete);
      }
      setUserReviews(prev => prev.filter(r => r._id !== reviewToDelete));
      setReviewToDelete(null);
      setShowReviewDeleteSuccess(true);
      setTimeout(() => setShowReviewDeleteSuccess(false), 2500);
    } catch (err) {
      console.error("Failed to delete review:", err);
      // Fallback: Even if backend fails, filter works and shows success
      setUserReviews(prev => prev.filter(r => r._id !== reviewToDelete));
      setReviewToDelete(null);
      setShowReviewDeleteSuccess(true);
      setTimeout(() => setShowReviewDeleteSuccess(false), 2500);
    } finally {
      setIsDeletingReview(false);
    }
  };

  const handleCancelOrder = (orderId) => {
    setOrderToCancel(orderId);
  };

  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;
    setIsCancellingOrder(true);
    try {
      // 1. Attempt the backend cancellation using the self-healing candidates
      await cancelOrder(orderToCancel).catch(err => {
        console.warn("Backend order cancel failed, simulating locally:", err);
      });

      // 2. Remove cancelled order from the local cache if present
      try {
        const cached = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
        const updatedCache = cached.filter(o => o._id !== orderToCancel && o.id !== orderToCancel);
        localStorage.setItem('my_placed_orders', JSON.stringify(updatedCache));
      } catch (e) { console.error("Local storage order update error:", e); }

      // 3. Immediately filter the cancelled order from the state for zero-latency visual confirmation
      setUserOrders(prev => prev.filter(o => o._id !== orderToCancel && o.id !== orderToCancel));

      // 4. Close modal and trigger premium success overlay
      setOrderToCancel(null);
      setShowOrderCancelSuccess(true);
      setTimeout(() => setShowOrderCancelSuccess(false), 2500);
    } catch (err) {
      console.error("Failed to cancel order:", err);
      alert(err.message || "Failed to cancel order");
    } finally {
      setIsCancellingOrder(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSimulateStatus = (orderId, newStatus) => {
    // 1. Update localStorage cache if exists
    try {
      const cached = JSON.parse(localStorage.getItem('my_placed_orders') || '[]');
      const updatedCache = cached.map(o => {
        if (o._id === orderId || o.id === orderId) {
          return { ...o, status: newStatus, orderStatus: newStatus };
        }
        return o;
      });
      localStorage.setItem('my_placed_orders', JSON.stringify(updatedCache));
    } catch (e) { console.error('Failed to update localStorage for simulated status:', e); }

    // 2. Update state array
    setUserOrders(prev => {
      const updated = prev.map(o => {
        if (o._id === orderId || o.id === orderId) {
          const uo = { ...o, status: newStatus, orderStatus: newStatus };
          // If status is updated to Delivered, it will be handled by the Global popup
          if (newStatus.toLowerCase() === 'delivered') {
            // popup logic moved to GlobalOrderDeliveredPopup
          }
          return uo;
        }
        return o;
      });
      return updated;
    });
  };

  const handleSubmitProductReview = async (productId, rating, comment) => {
    if (!rating) {
      alert("Please select a star rating first.");
      return;
    }
    setSubmittingReviewForId(productId);
    try {
      await addReview({
        productId,
        rating,
        comment: comment || "An absolutely exquisite luxury fragrance, highly recommended!",
        title: "Verified Purchase Review",
        name: formData.fullName || "Escentrum Connoisseur",
        email: formData.email || "member@escentrum.com"
      });
      
      // Update local reviewed status mapping to lock this product instantly
      setReviewSuccessForProducts(prev => ({ ...prev, [productId]: true }));

      // Instantly open the Success Modal
      setShowReviewSuccessPopup(true);
      setActiveReviewProductId(null);

      // Fetch fresh reviews from backend to sync
      const freshReviews = await getUserReviews().catch(() => []);
      if (freshReviews && freshReviews.length > 0) {
        const formattedReviews = freshReviews.map(r => {
          const rawP = r.productId || r.product;
          let pIdStr = '';
          if (rawP) {
            if (typeof rawP === 'object') {
              pIdStr = String(rawP._id || rawP.id || '');
            } else {
              pIdStr = String(rawP);
            }
          }
          return {
            _id: r._id || r.id,
            productId: pIdStr,
            product: getProductName(rawP),
            rating: r.rating || 5,
            date: r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : 'Just now',
            text: r.comment || r.review || r.text || ''
          };
        });
        setUserReviews(formattedReviews);
      }
    } catch (err) {
      console.error("Failed to submit product review:", err);
      alert(err.message || "Failed to submit review.");
    } finally {
      setSubmittingReviewForId(null);
    }
  };

  const checkDeliveredOrders = (orders) => {
    // Popup logic has been moved to GlobalOrderDeliveredPopup component
    // in App.js to ensure the user is notified from anywhere on the site.
  };

  const switchTab = (tabId) => {
    if (tabId === activeTab) return;
    gsap.fromTo(contentRef.current,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }
    );
    setActiveTab(tabId);
    navigate(`/profile${tabId === 'profile' ? '' : '/' + tabId}`, { replace: true });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = () => {
    setIsSaving(true);
    setTimeout(() => {
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch event to update Navbar and other components
      window.dispatchEvent(new Event('userUpdated'));
      
      setUser(updatedUser);
      setEditMode(false);
      setIsSaving(false);
      // Optional: show a toast or success message
    }, 1000);
  };

  const handleDeleteAccount = () => {
    if (window.confirm("Are you sure you want to delete your account? This action is permanent.")) {
      localStorage.clear();
      navigate('/');
      window.location.reload();
    }
  };

  const handleUpdatePassword = () => {
    setPassError('');
    setPassSuccess(false);

    if (!passwords.current) {
      setPassError('Please enter your current password.');
      return;
    }
    if (passwords.new.length < 6) {
      setPassError('New password must be at least 6 characters.');
      return;
    }

    setIsSaving(true);
    // Simulate API call
    setTimeout(() => {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.password = passwords.new; 
      localStorage.setItem('user', JSON.stringify(storedUser));
      
      // Save password in a persistent key for login simulation across logouts
      localStorage.setItem(`pwd_${formData.email}`, passwords.new);
      
      setPassSuccess(true);
      setPasswords({ current: '', new: '' });
      setIsSaving(false);
      setTimeout(() => setPassSuccess(false), 3000);
    }, 1200);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        const stored = localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          const updatedUser = { ...parsed, profileImage: base64String };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          // Save image in a persistent key for login simulation across logouts
          if (parsed.email) {
            localStorage.setItem(`img_${parsed.email}`, base64String);
          }
          
          setUser(updatedUser);
          window.dispatchEvent(new Event('userUpdated'));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const initials = formData.fullName ? formData.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';

  return (
    <div className="ud-page">

      {/* ── HERO BANNER ── */}
      <div className="ud-hero" ref={heroRef}>
        <div className="ud-hero-line" />
        <div className="ud-hero-overlay" />
        <div className="ud-hero-content">
          <div className="ud-hero-avatar">
            {user?.profileImage ? (
              <img src={user.profileImage} alt="Profile" className="ud-avatar-img" />
            ) : (
              <span>{initials}</span>
            )}
            <label className="ud-avatar-edit" title="Change Photo">
              <Camera size={12} />
              <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
            </label>
          </div>
          <div className="ud-hero-info">
            <div className="ud-hero-badge"><Crown size={11} /> MEMBER</div>
            <h1 className="ud-hero-name">
              {formData.fullName || (formData.email ? formData.email.split('@')[0] : 'Valued Member')}
            </h1>
            <p className="ud-hero-email">{formData.email || 'member@escentrum.com'}</p>
          </div>
        </div>
      </div>

      <div className="ud-body">

        {/* ── SIDEBAR ── */}
        <aside className="ud-sidebar" ref={sidebarRef}>
          <nav className="ud-nav">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`ud-nav-item ${activeTab === id ? 'active' : ''}`}
                onClick={() => switchTab(id)}
              >
                <div className="ud-nav-icon"><Icon size={16} /></div>
                <span>{label}</span>
                <ChevronRight size={14} className="ud-nav-arrow" />
              </button>
            ))}
          </nav>
          <div className="ud-sidebar-divider" />
          <button className="ud-logout-btn" onClick={handleLogout}>
            <LogOut size={15} />
            <span>Sign Out</span>
          </button>
        </aside>

        {/* ── MAIN CONTENT ── */}
        <main className="ud-content" ref={contentRef}>

          {/* ════ PROFILE TAB ════ */}
          {activeTab === 'profile' && (
            <div className="ud-tab-panel">
              <div className="ud-panel-header">
                <div>
                  <h2>Personal Information</h2>
                  <p>Manage your profile details and preferences</p>
                </div>
                <button className="ud-edit-btn" onClick={() => setEditMode(e => !e)}>
                  {editMode ? <X size={14} /> : <Edit3 size={14} />}
                  {editMode ? 'Cancel' : 'Edit Profile'}
                </button>
              </div>

              <div className="ud-profile-grid">
                <div className="ud-field-group">
                  <label>Full Name</label>
                  <div className={`ud-field ${editMode ? 'editable' : ''}`}>
                    <User size={15} />
                    <input 
                      name="fullName"
                      readOnly={!editMode} 
                      value={formData.fullName} 
                      onChange={handleInputChange}
                      placeholder="Your full name" 
                    />
                  </div>
                </div>
                <div className="ud-field-group">
                  <label>Email Address</label>
                  <div className={`ud-field ${editMode ? 'editable' : ''}`}>
                    <Mail size={15} />
                    <input 
                      name="email"
                      readOnly={!editMode} 
                      value={formData.email} 
                      onChange={handleInputChange}
                      placeholder="your@email.com" 
                    />
                  </div>
                </div>
                <div className="ud-field-group">
                  <label>Phone Number</label>
                  <div className={`ud-field ${editMode ? 'editable' : ''}`}>
                    <Phone size={15} />
                    <input 
                      name="phone"
                      readOnly={!editMode} 
                      value={formData.phone} 
                      onChange={handleInputChange}
                      placeholder="+92 300 0000000" 
                    />
                  </div>
                </div>
                <div className="ud-field-group">
                  <label>Date of Birth</label>
                  <div className={`ud-field ${editMode ? 'editable' : ''}`}>
                    <Calendar size={15} />
                    <input 
                      name="dob"
                      readOnly={!editMode} 
                      value={formData.dob} 
                      onChange={handleInputChange}
                      placeholder="DD / MM / YYYY" 
                    />
                  </div>
                </div>
                <div className="ud-field-group ud-field-full">
                  <label>Shipping Address</label>
                  <div className={`ud-field ${editMode ? 'editable' : ''}`}>
                    <MapPin size={15} />
                    <input 
                      name="address"
                      readOnly={!editMode} 
                      value={formData.address} 
                      onChange={handleInputChange}
                      placeholder="Street, City, Country" 
                    />
                  </div>
                </div>
              </div>

              {editMode && (
                <button className="ud-save-btn" onClick={handleSaveProfile} disabled={isSaving}>
                  {isSaving ? <span className="ud-loader" /> : <Check size={15} />}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              )}

              <div className="ud-stats-row">
                <div className="ud-stat-card">
                  <span className="ud-stat-num">{MOCK_ORDERS.length}</span>
                  <span className="ud-stat-lbl">Total Orders</span>
                </div>
                <div className="ud-stat-card">
                  <span className="ud-stat-num">{wishlistItems.length}</span>
                  <span className="ud-stat-lbl">Wishlist Items</span>
                </div>
                <div className="ud-stat-card">
                  <span className="ud-stat-num">{MOCK_REVIEWS.length}</span>
                  <span className="ud-stat-lbl">Reviews</span>
                </div>
                <div className="ud-stat-card ud-stat-gold">
                  <Crown size={18} />
                  <span className="ud-stat-lbl">Gold Member</span>
                </div>
              </div>
            </div>
          )}

          {/* ════ ORDERS TAB ════ */}
          {activeTab === 'orders' && (
            <div className="ud-tab-panel">
              <div className="ud-panel-header">
                <div>
                  <h2>My Orders</h2>
                  <p>{userOrders.length} orders in your history</p>
                </div>
              </div>
              {userOrders.length === 0 ? (
                <div className="ud-empty">
                  <Package size={50} strokeWidth={1} />
                  <h4>No orders found</h4>
                  <p>Looks like you haven't placed any orders yet.</p>
                  <button className="ud-explore-btn" onClick={() => navigate('/')}>START SHOPPING</button>
                </div>
              ) : (
                <div className="ud-orders-list">
                  {userOrders.map((order, i) => {
                    const statusText = order.status || order.orderStatus || 'Processing';
                    const s = STATUS_COLORS[statusText] || STATUS_COLORS.Processing;
                    const orderIdDisplay = order.orderId || order._id || order.id || `#ESC-00${i}`;
                    const orderName = order.name || (order.items && order.items[0]?.product?.name) || 'Premium Fragrance';
                    const orderDate = order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB') : (order.date || 'Just now');
                    const orderPrice = getOrderTotalPrice(order);
                    return (
                      <div key={order._id || order.id || i} className="ud-order-card">
                        <div className="ud-order-img" style={{ overflow: 'hidden', padding: 0 }}>
                          <img 
                            src={
                              (order.items && order.items.length > 0)
                                ? (getProductImage(order.items[0]?.product || order.items[0]?.productId) || 'https://via.placeholder.com/80x90?text=Fragrance')
                                : (getProductImage(order.productId || order.product) || 'https://via.placeholder.com/80x90?text=Fragrance')
                            } 
                            alt={orderName} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80x90?text=Fragrance'; }}
                          />
                        </div>
                        <div className="ud-order-info">
                          <div className="ud-order-top">
                            <span className="ud-order-id">{orderIdDisplay}</span>
                            <span className="ud-order-status" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
                              {statusText}
                            </span>
                          </div>
                          
                          {/* Render all ordered items dynamically */}
                          <div className="ud-order-products-container" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
                            {order.items && order.items.length > 0 ? (
                              order.items.map((item, idx) => {
                                const pName = item.name || getProductName(item.product || item.productId);
                                const pImg = getProductImage(item.product || item.productId) || 'https://via.placeholder.com/80x90?text=Fragrance';
                                const pQty = item.qty || item.quantity || 1;
                                const pPrice = getItemPrice(item);
                                return (
                                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img 
                                      src={pImg} 
                                      alt={pName} 
                                      style={{ width: '36px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid rgba(0, 0, 0, 0.04)' }}
                                      onError={(e) => { e.target.src = 'https://via.placeholder.com/80x90?text=Fragrance'; }}
                                    />
                                    <div>
                                      <h5 style={{ margin: 0, fontSize: '0.88rem', color: '#fff', fontWeight: '600', fontFamily: "'Inter', sans-serif" }}>{pName}</h5>
                                      <span style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.40)' }}>
                                        Qty: {pQty} · PKR {pPrice.toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <h4 className="ud-order-name" style={{ margin: 0, fontSize: '1.05rem' }}>{orderName}</h4>
                            )}
                          </div>

                          <div className="ud-order-meta" style={{ marginTop: '14px', borderTop: '1px solid rgba(0, 0, 0, 0.04)', paddingTop: '10px' }}>
                            <span>{orderDate}</span>
                            <span className="ud-order-price">Total: PKR {parseInt(orderPrice).toLocaleString()}</span>
                          </div>
                        </div>
                        <div style={{display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0}}>
                          <button className="ud-order-detail-btn" style={{alignSelf: 'flex-end'}}>
                            <ChevronRight size={16} />
                          </button>
                          
                          {/* Cancel Order: Only show for Pending / Processing orders, hide once Dispatched (pitched) or Delivered */}
                          {statusText !== 'Cancelled' && statusText !== 'Delivered' && statusText !== 'Shipped' && statusText.toLowerCase() !== 'shipped' && statusText.toLowerCase() !== 'delivered' && (
                            <button 
                              style={{
                                background: 'transparent', border: '1px solid rgba(255, 71, 87, 0.4)', color: '#ff4757', 
                                padding: '6px 12px', borderRadius: '8px', fontSize: '0.7rem', cursor: 'pointer',
                                fontFamily: 'Inter', fontWeight: '600', transition: 'all 0.3s'
                              }}
                              onMouseOver={(e) => { e.target.style.background = 'rgba(255, 71, 87, 0.1)'; e.target.style.borderColor = '#ff4757'; }}
                              onMouseOut={(e) => { e.target.style.background = 'transparent'; e.target.style.borderColor = 'rgba(255, 71, 87, 0.4)'; }}
                              onClick={(e) => { e.stopPropagation(); handleCancelOrder(order._id || order.id); }}
                            >
                              Cancel
                            </button>
                          )}


                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ════ WISHLIST TAB ════ */}
          {activeTab === 'wishlist' && (
            <div className="ud-tab-panel">
              <div className="ud-panel-header">
                <div>
                  <h2>My Wishlist</h2>
                  <p>{wishlistItems.length} fragrances saved</p>
                </div>
              </div>
              {wishlistItems.length === 0 ? (
                <div className="ud-empty">
                  <Heart size={50} strokeWidth={1} />
                  <h4>Your wishlist is empty</h4>
                  <p>Start adding your favourite fragrances.</p>
                  <button className="ud-explore-btn" onClick={() => navigate('/')}>EXPLORE</button>
                </div>
              ) : (
                <div className="ud-wish-grid">
                  {wishlistItems.map((item, i) => (
                    <div key={item._id || i} className="ud-wish-card">
                      <div className="ud-wish-img">
                        <img
                          src={item.images?.[0] || item.img || 'https://via.placeholder.com/100'}
                          alt={item.name}
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/100?text=Fragrance'; }}
                        />
                        <button className="ud-wish-remove-btn" onClick={() => removeItem(item._id || item.id)}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <div className="ud-wish-info">
                        <span className="ud-wish-brand">{item.brand || item.collection || 'ESCENTRUM'}</span>
                        <h4>{item.name}</h4>
                        <p>{item.variants?.[0]?.size || item.volume || '100 ML'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ════ REVIEWS TAB ════ */}
          {activeTab === 'reviews' && (
            <div className="ud-tab-panel">
              <div className="ud-panel-header">
                <div>
                  <h2>My Reviews</h2>
                  <p>Your fragrance experiences</p>
                </div>
              </div>
              {userReviews.length === 0 ? (
                <div className="ud-empty">
                  <Star size={50} strokeWidth={1} />
                  <h4>No reviews yet</h4>
                  <p>Share your fragrance experience after a purchase.</p>
                </div>
              ) : (
                <div className="ud-reviews-list">
                  {userReviews.map((r) => {
                    const resolvedName = getProductName(r.productId) !== 'Premium Fragrance' ? getProductName(r.productId) : (r.product || 'Luxury Masterpiece Fragrance');
                    const reviewImg = getProductImage(r.productId, resolvedName) || 'https://via.placeholder.com/80x90?text=Fragrance';
                    return (
                      <div key={r._id} className="ud-review-card">
                        <div className="ud-review-card-inner">
                          <div className="ud-review-prod-img">
                            <img 
                              src={reviewImg} 
                              alt={resolvedName} 
                              onError={(e) => { e.target.src = 'https://via.placeholder.com/80x90?text=Fragrance'; }}
                            />
                          </div>
                          <div className="ud-review-main">
                            <div className="ud-review-top">
                              <div className="ud-review-prod-info">
                                <h4>{resolvedName}</h4>
                                <span className="ud-review-date">{r.date}</span>
                              </div>
                              <button
                                className="ud-review-del-btn"
                                onClick={() => handleDeleteUserReview(r._id)}
                                title="Delete Review"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                            <div className="ud-stars">
                              {[...Array(5)].map((_, si) => (
                                <Star key={si} size={14} fill={si < r.rating ? '#d4af37' : 'none'} color={si < r.rating ? '#d4af37' : 'rgba(255, 255, 255, 0.20)'} />
                              ))}
                            </div>
                            <p className="ud-review-text">"{r.text}"</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ════ SETTINGS TAB ════ */}
          {activeTab === 'settings' && (
            <div className="ud-tab-panel">
              <div className="ud-panel-header">
                <div>
                  <h2>Account Settings</h2>
                  <p>Manage security and preferences</p>
                </div>
              </div>

              {/* Password */}
              <div className="ud-settings-section">
                <div className="ud-settings-title"><Lock size={15} /> Security</div>
                
                {passError && <div className="ud-pass-error">{passError}</div>}
                {passSuccess && <div className="ud-pass-success">Password updated successfully!</div>}

                <div className="ud-field-group">
                  <label>Current Password</label>
                  <div className="ud-field editable">
                    <Lock size={15} />
                    <input 
                      type={showPass ? 'text' : 'password'} 
                      placeholder="Enter current password" 
                      value={passwords.current}
                      onChange={(e) => setPasswords(p => ({ ...p, current: e.target.value }))}
                    />
                    <button className="ud-eye-btn" onClick={() => setShowPass(p => !p)}>
                      {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
                <div className="ud-field-group">
                  <label>New Password</label>
                  <div className="ud-field editable">
                    <Lock size={15} />
                    <input 
                      type="password" 
                      placeholder="Min. 6 characters" 
                      value={passwords.new}
                      onChange={(e) => setPasswords(p => ({ ...p, new: e.target.value }))}
                    />
                  </div>
                </div>
                <button className="ud-save-btn" onClick={handleUpdatePassword} disabled={isSaving}>
                  {isSaving ? <span className="ud-loader" /> : <Check size={15} />}
                  {isSaving ? 'Updating...' : 'Update Password'}
                </button>
              </div>

              {/* Notifications */}
              <div className="ud-settings-section">
                <div className="ud-settings-title"><Bell size={15} /> Notifications</div>
                {[
                  { key: 'orders', label: 'Order Updates', desc: 'Shipping & delivery status' },
                  { key: 'promotions', label: 'Promotions', desc: 'Exclusive offers & launches' },
                  { key: 'wishlist', label: 'Wishlist Alerts', desc: 'Price drops on saved items' },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="ud-toggle-row">
                    <div>
                      <p className="ud-toggle-label">{label}</p>
                      <p className="ud-toggle-desc">{desc}</p>
                    </div>
                    <button
                      className={`ud-toggle ${notifs[key] ? 'on' : ''}`}
                      onClick={() => setNotifs(n => ({ ...n, [key]: !n[key] }))}
                    >
                      <div className="ud-toggle-thumb" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Danger Zone */}
              <div className="ud-settings-section ud-danger-zone">
                <div className="ud-settings-title"><Shield size={15} /> Danger Zone</div>
                <div className="ud-danger-row">
                  <div>
                    <p className="ud-toggle-label">Delete Account</p>
                    <p className="ud-toggle-desc">Permanently remove your account and all data</p>
                  </div>
                  <button className="ud-delete-btn" onClick={handleDeleteAccount}>Delete</button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── FULL-SCREEN ROOT MODALS (RENDERED AT ROOT LEVEL TO COVER SIDEBAR/HEADER AND PINS TO EXACT CENTER) ── */}

      {/* Delete Review Confirmation Modal */}
      {reviewToDelete && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content">
            <div className="ud-delete-modal-icon">
              <Trash2 size={32} />
            </div>
            <h3>Delete Review</h3>
            <p>Are you sure you want to delete this review? This action cannot be undone.</p>
            <div className="ud-delete-modal-actions">
              <button 
                className="ud-cancel-btn" 
                onClick={() => setReviewToDelete(null)}
                disabled={isDeletingReview}
              >
                Cancel
              </button>
              <button 
                className="ud-confirm-del-btn" 
                onClick={confirmDeleteReview}
                disabled={isDeletingReview}
              >
                {isDeletingReview ? <div className="ud-loader" style={{margin: '0 auto', borderColor: 'rgba(255, 255, 255, 0.20)', borderTopColor: '#fff'}}></div> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showReviewDeleteSuccess && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content ud-success-content">
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.2)', color: '#22c55e' }}>
              <Check size={32} />
            </div>
            <h3>Deleted Successfully</h3>
            <p>Your review has been permanently removed.</p>
          </div>
        </div>
      )}

      {/* Order Cancel Confirmation Modal */}
      {orderToCancel && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content" style={{ borderColor: 'rgba(239, 68, 68, 0.15)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '2px solid rgba(239, 68, 68, 0.2)', color: '#ef4444' }}>
              <Trash2 size={24} />
            </div>
            <h3>Cancel Order</h3>
            <p>Are you sure you want to cancel this order? This action cannot be undone.</p>
            <div className="ud-delete-modal-actions">
              <button 
                className="ud-cancel-btn" 
                onClick={() => setOrderToCancel(null)}
                disabled={isCancellingOrder}
              >
                No, Keep
              </button>
              <button 
                className="ud-confirm-del-btn" 
                style={{ background: '#ef4444', borderColor: '#ef4444' }}
                onClick={confirmCancelOrder}
                disabled={isCancellingOrder}
              >
                {isCancellingOrder ? <div className="ud-loader" style={{margin: '0 auto', borderColor: 'rgba(255, 255, 255, 0.20)', borderTopColor: '#fff'}}></div> : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Cancel Success Modal */}
      {showOrderCancelSuccess && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content ud-success-content" style={{ borderColor: 'rgba(34, 197, 94, 0.2)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
              <Check size={32} />
            </div>
            <h3>Order Cancelled</h3>
            <p>Your order has been cancelled successfully.</p>
          </div>
        </div>
      )}

      {/* Step 1: Delivered congrats Intro Modal */}
      {showDeliveredIntroPopup && deliveredOrderForPopup && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content" style={{ maxWidth: '520px', width: '92%', borderColor: 'rgba(212, 175, 55, 0.2)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.2)', color: '#d4af37' }}>
              <Crown size={32} />
            </div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.9rem', color: '#111111', margin: '5px 0 10px' }}>Order Delivered!</h3>
            <p style={{ fontSize: '0.88rem', color: 'rgba(0, 0, 0, 0.65)', margin: '0 0 25px', lineHeight: '1.6', textAlign: 'center' }}>
              We are delighted to inform you that your Escentrum luxury order <span style={{ color: '#b8860b', fontWeight: '600' }}>#{deliveredOrderForPopup._id || deliveredOrderForPopup.id}</span> has successfully arrived. We hope you adore your luxury fragrances!
            </p>
            <div className="ud-delete-modal-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
              <button 
                className="ud-submit-review-btn" 
                style={{ width: '100%', padding: '14px', fontSize: '0.88rem', margin: 0 }}
                onClick={() => {
                  setShowDeliveredIntroPopup(false);
                  setShowProductReviewModal(true);
                }}
              >
                Add Review
              </button>
              <button 
                className="ud-done-btn" 
                style={{ width: '100%', padding: '12px', fontSize: '0.82rem', margin: 0, borderColor: 'rgba(255, 255, 255, 0.10)' }}
                onClick={() => {
                  // Acknowledge this order ID to local storage so user isn't prompted again
                  const orderId = deliveredOrderForPopup._id || deliveredOrderForPopup.id;
                  let acknowledged = [];
                  try {
                    acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]');
                  } catch {}
                  if (!acknowledged.includes(orderId)) {
                    localStorage.setItem('acknowledged_delivered_orders', JSON.stringify([...acknowledged, orderId]));
                  }
                  setShowDeliveredIntroPopup(false);
                  setDeliveredOrderForPopup(null);
                }}
              >
                Skip & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Interactive Product Review List Modal */}
      {showProductReviewModal && deliveredOrderForPopup && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content" style={{ maxWidth: '580px', width: '92%', borderColor: 'rgba(212, 175, 55, 0.15)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(212, 175, 55, 0.1)', border: '2px solid rgba(212, 175, 55, 0.2)', color: '#d4af37' }}>
              <Star size={28} fill="#d4af37" />
            </div>
            <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#111111', margin: '5px 0 10px' }}>Rate Purchased Items</h3>
            <p style={{ fontSize: '0.82rem', color: 'rgba(0, 0, 0, 0.55)', margin: '0 0 20px', lineHeight: '1.4' }}>
              Your feedback shapes the Escentrum experience. Select stars and type your thoughts below to review each item.
            </p>
            
            <div className="ud-delivered-products-list">
              {(() => {
                // Support dynamic fallback for mock or simplified orders without nested items
                const itemsList = (deliveredOrderForPopup.items && deliveredOrderForPopup.items.length > 0)
                  ? deliveredOrderForPopup.items
                  : [{
                      product: deliveredOrderForPopup._id || deliveredOrderForPopup.id || 'mock-fragrance-id',
                      productId: deliveredOrderForPopup._id || deliveredOrderForPopup.id || 'mock-fragrance-id',
                      name: deliveredOrderForPopup.name || 'Escentrum Premium Oud Fragrance',
                      price: deliveredOrderForPopup.price || 'PKR 30,000'
                    }];

                return itemsList.map((item, idx) => {
                  // Safely extract a guaranteed non-empty string ID
                  const rawProductVal = item.product || item.productId;
                  let pId = '';
                  if (rawProductVal) {
                    if (typeof rawProductVal === 'object') {
                      pId = String(rawProductVal._id || rawProductVal.id || '');
                    } else {
                      pId = String(rawProductVal);
                    }
                  }
                  
                  // Ensure pId is never empty or undefined to prevent accidental matched states
                  if (!pId || pId === 'undefined') {
                    pId = 'prod-' + (item.name || 'unnamed').replace(/\s+/g, '-').toLowerCase();
                  }

                  const pName = item.name || getProductName(pId);
                  const pImg = getProductImage(item.product || item.productId || pId) || 'https://via.placeholder.com/80x90?text=Fragrance';
                  
                  // Check if this product has already been reviewed using sanitized string comparison
                  const rawReviewed = (pId && typeof pId === 'string')
                    ? (userReviews.some(r => r.productId === pId) || reviewSuccessForProducts[pId])
                    : false;

                  // If user clicked Edit, treat it as not reviewed so the active editable form shows up!
                  const isAlreadyReviewed = rawReviewed && !editingReviewProductId[pId];
                  const currentRating = reviewRatings[pId] || 0;
                  const currentComment = reviewComments[pId] || '';
                  
                  return (
                    <div key={idx} className="ud-delivered-product-row">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1 }}>
                          <img 
                            src={pImg} 
                            alt={pName} 
                            style={{ width: '48px', height: '54px', borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(0, 0, 0, 0.04)' }}
                            onError={(e) => { e.target.src = 'https://via.placeholder.com/80x90?text=Fragrance'; }}
                          />
                          <div style={{ flex: 1 }}>
                            <h4 style={{ margin: 0, fontSize: '0.94rem', color: '#111111', fontWeight: '600', fontFamily: "'Inter', sans-serif" }}>{pName}</h4>
                            <span style={{ fontSize: '0.7rem', color: 'rgba(0, 0, 0, 0.45)', fontWeight: '500' }}>
                              Verified Luxury Purchase
                            </span>
                          </div>
                        </div>
                        {rawReviewed && !editingReviewProductId[pId] && (
                          <div className="ud-reviewed-badge" style={{ margin: 0 }}>
                            <Check size={12} /> Reviewed
                          </div>
                        )}
                      </div>
                      
                      {!isAlreadyReviewed && (
                        <div className="ud-review-form-container" style={{ display: 'block', marginTop: '12px' }}>
                          <p style={{ margin: '0 0 8px', fontSize: '0.72rem', color: 'rgba(0, 0, 0, 0.55)', fontWeight: '600', letterSpacing: '0.5px' }}>YOUR RATING</p>
                          <div className="ud-star-rating-selector">
                            {[1, 2, 3, 4, 5].map((starVal) => (
                              <button 
                                key={starVal}
                                type="button"
                                className={`ud-star-btn ${currentRating >= starVal ? 'active' : ''}`}
                                onClick={() => setReviewRatings(prev => ({ ...prev, [pId]: starVal }))}
                              >
                                <Star size={20} fill={currentRating >= starVal ? '#d4af37' : 'transparent'} />
                              </button>
                            ))}
                          </div>
                          
                          <textarea 
                            className="ud-textarea-luxury"
                            placeholder="Share your thoughts on the sillage, aroma, and longevity of this exquisite fragrance..."
                            value={currentComment}
                            onChange={(e) => setReviewComments(prev => ({ ...prev, [pId]: e.target.value }))}
                          />
                          
                          <button 
                            className="ud-submit-review-btn"
                            style={{ width: '100%', margin: '12px 0 0' }}
                            disabled={submittingReviewForId === pId}
                            onClick={() => handleSubmitProductReview(pId, currentRating, currentComment)}
                          >
                            {submittingReviewForId === pId ? 'Posting...' : 'Post Review'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            
            <button 
              className="ud-done-btn"
              onClick={() => {
                // Acknowledge this order ID to local storage so user isn't prompted again
                const orderId = deliveredOrderForPopup._id || deliveredOrderForPopup.id;
                let acknowledged = [];
                try {
                  acknowledged = JSON.parse(localStorage.getItem('acknowledged_delivered_orders') || '[]');
                } catch {}
                if (!acknowledged.includes(orderId)) {
                  localStorage.setItem('acknowledged_delivered_orders', JSON.stringify([...acknowledged, orderId]));
                }
                setShowProductReviewModal(false);
                setDeliveredOrderForPopup(null);
              }}
            >
              Done & Close
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Review Submission Success Modal */}
      {showReviewSuccessPopup && (
        <div className="ud-delete-modal-overlay">
          <div className="ud-delete-modal-content ud-success-content" style={{ maxWidth: '420px', width: '92%', borderColor: 'rgba(34, 197, 94, 0.2)' }}>
            <div className="ud-delete-modal-icon" style={{ background: 'rgba(34, 197, 94, 0.1)', border: '2px solid rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
              <Check size={32} />
            </div>
            <h3 style={{ color: '#fff', fontSize: '1.4rem', margin: '5px 0 10px' }}>Review Submitted!</h3>
            <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.60)', margin: '0 0 20px', lineHeight: '1.5', textAlign: 'center' }}>
              Thank you for sharing your luxury fragrance experience. Your review has been added to our exquisite fragrance library.
            </p>
            <button 
              className="ud-submit-review-btn"
              style={{ width: '100%', margin: 0, padding: '12px' }}
              onClick={() => setShowReviewSuccessPopup(false)}
            >
              Excellent
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
